import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { setOfferteDiscount, type OfferteDiscount } from '../../lib/offertes';
import {
  DISCOUNT_REASON_OPTIONS,
  REASON_NOTE_MAX,
  reasonOption,
  type DiscountReason,
} from '../../lib/discount-constants';

interface OfferteKortingPillProps {
  leadId: string;
  discount: OfferteDiscount;
  canEdit: boolean;
  onSaved: () => void;
}

/**
 * Pill in de offerte-header die de actieve korting toont en — bij klikken — een
 * mini-popover opent om het percentage te wijzigen of te verwijderen. Lead-
 * tegenhanger van KortingPill (order-detail). Campagne-codes vereisen een
 * expliciete vervang-bevestiging.
 */
export function OfferteKortingPill({ leadId, discount, canEdit, onSaved }: OfferteKortingPillProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isCampaign = discount.kind === 'campaign';
  const isToeslag = discount.kind === 'toeslag' && (discount.percentage ?? 0) > 0;
  const hasActive = (discount.kind === 'manual' || discount.kind === 'toeslag') && (discount.percentage ?? 0) > 0;
  const [mutType, setMutType] = useState<'korting' | 'toeslag'>(isToeslag ? 'toeslag' : 'korting');
  // Reden is verplicht bij toepassen — de API weigert zonder. Bewust geen
  // default: een voorgeselecteerde reden is geen geregistreerde reden.
  const [reason, setReason] = useState<DiscountReason | ''>('');
  const [reasonNote, setReasonNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setInput(hasActive ? String(discount.percentage) : '');
    setMutType(isToeslag ? 'toeslag' : 'korting');
    setReason('');
    setReasonNote('');
    setError(null);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, hasActive, isToeslag, discount.percentage]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const save = async (rawPercentage: number | null) => {
    if (pending) return;
    // Verwijderen mag zonder reden; toepassen niet.
    if (rawPercentage !== null && !reason) {
      setError('Kies een reden');
      return;
    }
    const selected = reasonOption(reason || null);
    const trimmedNote = reasonNote.trim();
    if (rawPercentage !== null && selected?.noteRequired && !trimmedNote) {
      setError('Licht kort toe waarom');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await setOfferteDiscount(leadId, rawPercentage, {
        overrideCampaign: isCampaign,
        type: mutType,
        ...(rawPercentage !== null && reason ? { reason } : {}),
        ...(trimmedNote ? { reasonNote: trimmedNote } : {}),
      });
      onSaved();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim().replace(',', '.');
    if (!trimmed) {
      await save(null);
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Geef een geldig getal op');
      return;
    }
    await save(parsed > 0 ? parsed : null);
  };

  const pillClass = clsx(
    'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium border',
    hasActive && isToeslag
      ? 'bg-sky-50 text-sky-700 border-sky-200'
      : hasActive
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : isCampaign
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-slate-50 text-slate-500 border-slate-200',
    canEdit && 'cursor-pointer hover:ring-1 hover:ring-slate-300',
    pending && 'opacity-60'
  );

  const label = (() => {
    if (isToeslag) return `+${discount.percentage}% toeslag`;
    if (hasActive) return `−${discount.percentage}% korting`;
    if (isCampaign) return `Campagne: ${discount.code}`;
    return 'Geen korting';
  })();

  if (!canEdit) {
    return <span className={pillClass}>{label}</span>;
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        className={pillClass}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {pending && (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
          </svg>
        )}
        <span>{label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            {isCampaign && (
              <div className="rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-1.5">
                <strong>Vervangt campagne:</strong> {discount.code}. De handmatige korting wint na opslaan.
              </div>
            )}
            <div className="flex gap-1">
              {(['korting', 'toeslag'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setMutType(t)}
                  disabled={pending}
                  className={clsx(
                    'flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors',
                    mutType === t
                      ? t === 'toeslag'
                        ? 'border-sky-500 bg-sky-50 text-sky-700'
                        : 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  )}
                >
                  {t === 'korting' ? 'Korting' : 'Toeslag'}
                </button>
              ))}
            </div>
            <label className="text-xs font-medium text-slate-700">
              {mutType === 'toeslag' ? 'Toeslagpercentage' : 'Kortingspercentage'}
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="0"
                disabled={pending}
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>

            <label className="text-xs font-medium text-slate-700">Reden</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as DiscountReason | '')}
              disabled={pending}
              className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Kies een reden…</option>
              {DISCOUNT_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {reasonOption(reason || null) ? (
              <p className="text-[11px] text-slate-400 -mt-1">{reasonOption(reason || null)!.hint}</p>
            ) : null}
            {reason ? (
              <input
                type="text"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                maxLength={REASON_NOTE_MAX}
                disabled={pending}
                placeholder={
                  reasonOption(reason)?.noteRequired ? 'Toelichting (verplicht)' : 'Toelichting (optioneel)'
                }
                className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ) : null}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={pending}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-1.5 rounded disabled:opacity-60"
              >
                Opslaan
              </button>
              {hasActive && (
                <button
                  type="button"
                  onClick={() => save(null)}
                  disabled={pending}
                  className="text-xs text-red-600 hover:text-red-700 disabled:opacity-60"
                >
                  Verwijderen
                </button>
              )}
            </div>
            {error && <p className="text-xs text-amber-700">⚠ {error}</p>}
            <p className="text-[11px] text-slate-400">
              De offerte wordt automatisch herprijsd. Bij akkoord loopt de {mutType} mee naar de order.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
